import { Link } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import AdBanner from '../components/AdBanner';
import { AD_SLOTS } from '../config/adSlots';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useStructuredData } from '../hooks/useStructuredData';
import { useAuthContext } from '../contexts/AuthContext';

export default function AboutPage() {
  const { isAuthenticated } = useAuthContext();
  useDocumentMeta(
    'About',
    'Learn about Takedown League, the free fantasy wrestling platform where fans draft NCAA wrestlers, compete in leagues, and earn points from live tournament results.'
  );

  useStructuredData({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Takedown League',
    url: 'https://takedownleague.com',
    description:
      'Takedown League is a free fantasy sports platform built for NCAA wrestling fans. Draft real wrestlers, compete in leagues with friends, and earn points based on live tournament results.',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'support@takedownleague.com',
      url: 'https://takedownleague.com/contact',
    },
  });

  return (
    <PublicLayout>
      <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-purple-700 text-white py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-4">About Takedown League</h1>
          <p className="text-xl text-purple-100">
            The fantasy sports platform built for wrestling fans.
          </p>
        </div>
      </div>

      <section className="bg-white py-12 px-4">
        <div className="max-w-3xl mx-auto prose prose-gray">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What Is Takedown League?</h2>
          <p className="text-gray-700 mb-4">
            Takedown League is a free fantasy sports platform designed specifically for NCAA
            wrestling. We support NCAA Regional (national qualifier) and National tournaments. Once
            seeds are released for a tournament, you can create a league, draft wrestlers to your
            roster using a budget system, and earn points based on the team points your wrestlers
            accumulate during the tournament.
          </p>
          <p className="text-gray-700 mb-4">
            Fantasy sports have long been popular in football, basketball, and baseball, but
            wrestling fans have been left out of the action. Takedown League was built to change
            that. Our platform brings the thrill of fantasy competition to the wrestling community,
            combining the strategy of roster building with the excitement of live tournament
            results.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-10">Who Is It For?</h2>
          <p className="text-gray-700 mb-4">
            Takedown League is for anyone who loves wrestling and wants to engage with the sport on
            a deeper level. Coaches use it to keep their teams motivated and invested in the broader
            wrestling community. Parents and fans use it to follow their favorite athletes through
            NCAA tournaments. Friend groups and wrestling clubs use it to add a layer of friendly
            competition to tournament time.
          </p>
          <p className="text-gray-700 mb-4">
            You do not need to be an expert to play. Takedown League is beginner-friendly, with a
            straightforward drafting process and a scoring system that rewards real wrestling
            outcomes. If you can follow a bracket, you can compete in Takedown League.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-10">How Scoring Works</h2>
          <p className="text-gray-700 mb-4">
            Your team's score is determined by the NCAA team points earned by each wrestler on your
            roster. This includes placing points, advancement points, and bonus points. Penalty
            deductions for team points will not be deducted from individual wrestlers' team points.
          </p>
          <ul className="list-disc pl-5 space-y-2 text-gray-700 mb-4">
            <li>
              <strong>Placement:</strong> Points are earned based on final placement in the
              tournament. First place earns the most (16 points), with decreasing points for lower
              placements.
            </li>
            <li>
              <strong>Advancement:</strong> Points are awarded as wrestlers advance through each
              round of the bracket, rewarding depth of competition.
            </li>
            <li>
              <strong>Byes:</strong> If a wrestler receives a bye and wins their next match, they
              earn advancement points for that progression — 1 point in the championship bracket or
              0.5 points in the consolation bracket. A bye alone (without a subsequent win) earns no
              points.
            </li>
            <li>
              <strong>Bonus:</strong> Additional points are awarded for pins, technical falls, and
              major decisions.
            </li>
          </ul>
          <p className="text-gray-700 mb-4">
            Scores are updated as the tournament progresses. The league member with the highest
            total score at the end of the tournament wins.
          </p>

          {/* Ad Banner - Mid-content placement */}
          <div className="my-8">
            <AdBanner slot={AD_SLOTS.about} format="horizontal" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-10">The Draft & Budget System</h2>
          <p className="text-gray-700 mb-4">
            Each team is given a budget for drafting wrestlers. A wrestler's cost is based on their
            seed and matches the placement points for that position. For example, a #1 seed costs
            $16, a #2 seed costs $12, and so on. We recommend a $67 budget, which provides enough to
            draft one wrestler from each seed 1 through 8 plus two unseeded wrestlers, but the
            commissioner can adjust this.
          </p>
          <p className="text-gray-700 mb-4">
            You may pick up to 10 wrestlers (one per weight class), but you do not have to fill
            every weight class. You can pick multiple wrestlers with the same seed if you prefer.
            For instance, you could skip top seeds and instead draft four #4 seeds.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-10">Our Mission</h2>
          <p className="text-gray-700 mb-4">
            Wrestling is one of the oldest and most demanding sports in the world, yet it receives
            far less attention than mainstream team sports. Takedown League exists to build
            community around wrestling and give fans a reason to follow more athletes, more
            tournaments, and more weight classes.
          </p>
          <p className="text-gray-700 mb-4">
            We believe that fantasy sports can increase engagement, broaden fan interest, and bring
            the wrestling community closer together. By giving fans a personal stake in tournament
            outcomes, Takedown League transforms casual viewers into active participants who follow
            every match with purpose.
          </p>
          <p className="text-gray-700 mb-6">
            Takedown League is completely free to use. There are no entry fees, no paid advantages,
            and no gambling elements. Our goal is to grow the wrestling fan base and make every
            tournament more exciting for everyone involved.
          </p>
        </div>
      </section>

      <section className="bg-gradient-to-r from-blue-600 via-purple-600 to-purple-700 py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          {isAuthenticated ? (
            <>
              <h2 className="text-3xl font-bold text-white mb-4">Back to Your Leagues</h2>
              <p className="text-purple-100 mb-6">
                Head back to your leagues to manage your rosters and check standings.
              </p>
              <Link
                to="/leagues"
                className="inline-block bg-white text-purple-700 font-semibold py-3 px-8 rounded-lg hover:bg-purple-50 transition-colors text-lg"
              >
                Back to My Leagues
              </Link>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
              <p className="text-purple-100 mb-6">
                Create your free account and start building your dream wrestling roster today.
              </p>
              <Link
                to="/login?tab=register"
                className="inline-block bg-white text-purple-700 font-semibold py-3 px-8 rounded-lg hover:bg-purple-50 transition-colors text-lg"
              >
                Create Your Free Account
              </Link>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
